import axios from "axios";
import { API_SERVICE_ID, Url } from "../helper/constants";
import Header from "./Header";
import Encryption from "./Encryption";
import { store } from "../redux/store";
import { executeWithRetry } from "./requestRetry";

const parseAppApiResponse = (data) => {
  if (typeof data === "string") {
    return Encryption.decrypt(data);
  }
  return data;
};

const runRetriedRequest = (requestFactory, options = {}) =>
  executeWithRetry(
    ({ signal, timeout }) => requestFactory({ signal, timeout }),
    { signal: options.signal }
  );

const getRegistrationAccessToken = () => {
  const fromStore = store.getState().auth?.accessToken;
  if (fromStore) {
    return fromStore;
  }
  try {
    const saved = localStorage.getItem("data");
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    return (
      parsed?.signupAccessToken ||
      parsed?.signupResponse?.access_token ||
      parsed?.emailCodeCheck?.access_token ||
      null
    );
  } catch {
    return null;
  }
};

const pendingRegistrationStoreCards = new Map();
const completedRegistrationStoreCards = new Set();
const inFlightRequests = new Map();

const withInFlightDedup = (key, requestFactory) => {
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key);
  }

  const request = Promise.resolve()
    .then(requestFactory)
    .finally(() => {
      inFlightRequests.delete(key);
    });

  inFlightRequests.set(key, request);
  return request;
};
export default class AuthServices {
  // Check email
  static checkEmail(params) {
    return axios
      .post(`${Url}check-email-get-data`, Encryption.encrypt(params))
      .then((res) => {
        return Encryption.decrypt(res.data);
      });
  }

  // get registration code
  static getRegistrationCode() {
    return axios.get(`${Url}get-registration-code`).then((res) => {
      return Encryption.decrypt(res.data);
    });
  }

  // CheckEmailAndCode is valid or not
  static checkEmailAndCode(params) {
    return axios
      .post(`${Url}check-email-and-code`, Encryption.encrypt(params))
      .then((res) => parseAppApiResponse(res.data));
  }

  // Insurence detail

  static insurenceList(registrationCode, language = "en", options = {}) {
    const dedupeKey = `insurance-list:${registrationCode}:${language}`;
    return withInFlightDedup(dedupeKey, () =>
      runRetriedRequest(
        ({ signal, timeout }) =>
          axios.get(`${Url}common/insurance-list`, {
            params: {
              type: "register",
              registration_code: registrationCode,
            },
            headers: Header.getCommonRequestHeaders(language, API_SERVICE_ID),
            signal,
            timeout,
          }),
        options
      ).then((res) => parseAppApiResponse(res.data))
    );
  }

  static verifyInsuranceEligibility(params, language = "en") {
    const isFormData =
      typeof FormData !== "undefined" && params instanceof FormData;
    const headers = {
      ...Header.getCommonRequestHeaders(language, API_SERVICE_ID),
    };
    if (isFormData) {
      delete headers["Content-Type"];
    }

    return axios
      .post(
        `${Url}insurance/verify-eligibility`,
        isFormData ? params : Encryption.encrypt(params),
        { headers }
      )
      .then((res) => Encryption.decrypt(res.data));
  }

  static eligibleRdForInsurance(params, language = "en") {
    return axios
      .post(
        `${Url}registration/eligible-rd-for-insurance`,
        Encryption.encrypt(params),
        { headers: Header.getCommonRequestHeaders(language, API_SERVICE_ID) }
      )
      .then((res) => parseAppApiResponse(res.data));
  }

  static recheckInsuranceEstimate(params, language = "en") {
    const isFormData =
      typeof FormData !== "undefined" && params instanceof FormData;
    const headers = {
      ...Header.getCommonRequestHeaders(language, API_SERVICE_ID),
    };
    if (isFormData) {
      delete headers["Content-Type"];
    }

    return axios
      .post(
        `${Url}registration/recheck-insurance-estimate`,
        isFormData ? params : Encryption.encrypt(params),
        { headers }
      )
      .then((res) => parseAppApiResponse(res.data));
  }

  // Signup with all detail
  static signup(params, lng) {
    Header.setLangHeaders(lng);
    return axios.post(`${Url}signup`, params).then((res) => {
      return Encryption.decrypt(res.data);
    });
  }

  static appApiSignup(params, language = "en") {
    return axios
      .post(`${Url}signup`, Encryption.encrypt(params), {
        headers: Header.getCommonRequestHeaders(language, API_SERVICE_ID),
      })
      .then((res) => parseAppApiResponse(res.data));
  }

  // Register user get stream
  static registerUserGetStream(params) {
    Header.setHeaders();
    return axios.get(`${Url}register-user-getstream`).then((res) => {
      return Encryption.decrypt(res.data);
    });
  }

  // send message from RD
  static sendMessageFromRD(params) {
    Header.setHeaders();
    return axios.get(`${Url}common/send-message`).then((res) => {
      return Encryption.decrypt(res.data);
    });
  }

  //static pages
  static staticPages(params, language = "en", options = {}) {
    const { signal, timeout } = options;
    return axios
      .post(`${Url}common/static-page`, Encryption.encrypt(params), {
        headers: Header.getCommonRequestHeaders(language, API_SERVICE_ID),
        signal,
        timeout,
      })
      .then((res) => Encryption.decrypt(res.data));
  }

  // RD Slot List
  static rdSlotList(params) {
    Header.setHeaders();
    return axios
      .post(`${Url}list-rd-timeslot`, Encryption.encrypt(params))
      .then((res) => {
        return Encryption.decrypt(res.data);
      });
  }

  //Matched RD List
  static matchedRDList(params) {
    Header.setHeaders();
    Header.setTzHeader();

    return axios
      .post(`${Url}list-matched-rd`, Encryption.encrypt(params))
      .then((res) => {
        return Encryption.decrypt(res.data);
      });
  }

  //RD Slot List By ID
  static rdSlotListById(params, cT) {
    Header.setHeaders();
    return axios
      .post(`${Url}list-timeslot-by-rd`, Encryption.encrypt(params), cT)
      .then((res) => {
        return Encryption.decrypt(res.data);
      });
  }

  //filter-rd-common-list
  static commonFilterRDList(params) {
    Header.setHeaders();
    return axios
      .get(`${Url}common/filter-rd-common-list`, Encryption.encrypt(params))
      .then((res) => {
        return Encryption.decrypt(res.data);
      });
  }

  // Get Languages
  static getLanguages() {
    Header.setHeaders();
    return axios.get(`${Url}language/list`).then((res) => {
      return Encryption.decrypt(res.data);
    });
  }

  //Location List By RD ID
  static rdLocationListByRdId(params) {
    Header.setHeaders();
    return axios
      .post(`${Url}list-rd-appointment-location`, Encryption.encrypt(params))
      .then((res) => {
        return Encryption.decrypt(res.data);
      });
  }

  static changeLanguage(params) {
    return axios.post(
      `${Url}language/web-language-object`,
      Encryption.encrypt(params)
    );
  }

  // acknowlegment
  static acknowledgement(params, lng) {
    Header.setHeaders();
    Header.setLangHeaders(lng);
    return axios
      .get(`${Url}update-acknowledgement`, Encryption.encrypt(params, 1))
      .then((res) => {
        return Encryption.decrypt(res.data);
      });
  }

  //discovery queue
  static addToDiscoveryQueue() {
    Header.setHeaders();
    return axios.get(`${Url}add-client-to-discovery-queue`).then((res) => {
      return Encryption.decrypt(res.data);
    });
  }

  //stripe publish key
  static getStripePublishKey() {
    Header.setHeaders();
    return axios.get(`${Url}stripe/publishable-token`).then((res) => {
      return Encryption.decrypt(res.data);
    });
  }

  static getRegistrationStripePublishableToken(language = "en", options = {}) {
    const accessToken = getRegistrationAccessToken();
    if (!accessToken) {
      return Promise.reject(
        new Error("Session expired. Please restart registration.")
      );
    }
    const headers = {
      ...Header.getCommonRequestHeaders(language, API_SERVICE_ID),
      Authorization: accessToken,
    };
    return runRetriedRequest(
      ({ signal, timeout }) =>
        axios.get(`${Url}stripe/publishable-token`, {
          headers,
          signal,
          timeout,
        }),
      options
    ).then((res) => parseAppApiResponse(res.data));
  }

  static savePaymentType(params, language = "en") {
    const dedupeKey = `save-payment-type:${JSON.stringify(params)}:${language}`;
    return withInFlightDedup(dedupeKey, () => {
      const accessToken = getRegistrationAccessToken();
      if (!accessToken) {
        return Promise.reject(
          new Error("Session expired. Please restart registration.")
        );
      }
      const headers = {
        ...Header.getCommonRequestHeaders(language, API_SERVICE_ID),
        Authorization: accessToken,
      };
      return axios
        .post(`${Url}registration/save-payment-type`, Encryption.encrypt(params), {
          headers,
        })
        .then((res) => parseAppApiResponse(res.data));
    });
  }

  static createRegistrationSetupIntent(params = {}, language = "en", options = {}) {
    const accessToken = getRegistrationAccessToken();
    if (!accessToken) {
      return Promise.reject(
        new Error("Session expired. Please restart registration.")
      );
    }
    const headers = {
      ...Header.getCommonRequestHeaders(language, API_SERVICE_ID),
      Authorization: accessToken,
    };
    return runRetriedRequest(
      ({ signal, timeout }) =>
        axios.post(`${Url}stripe/create-setup-intent`, Encryption.encrypt(params), {
          headers,
          signal,
          timeout,
        }),
      options
    ).then((res) => parseAppApiResponse(res.data));
  }

  static storeRegistrationCard(params, language = "en") {
    const accessToken = getRegistrationAccessToken();
    if (!accessToken) {
      return Promise.reject(
        new Error("Session expired. Please restart registration.")
      );
    }

    const setupIntentId = params?.setup_intent_id;
    if (setupIntentId && completedRegistrationStoreCards.has(setupIntentId)) {
      return Promise.resolve({ status: 1 });
    }
    if (setupIntentId && pendingRegistrationStoreCards.has(setupIntentId)) {
      return pendingRegistrationStoreCards.get(setupIntentId);
    }

    const headers = {
      ...Header.getCommonRequestHeaders(language, API_SERVICE_ID),
      Authorization: accessToken,
    };
    const request = axios
      .post(`${Url}stripe/store-card`, Encryption.encrypt(params), { headers })
      .then((res) => parseAppApiResponse(res.data))
      .then((parsed) => {
        if (setupIntentId && parsed?.status) {
          completedRegistrationStoreCards.add(setupIntentId);
        }
        return parsed;
      })
      .finally(() => {
        if (setupIntentId) {
          pendingRegistrationStoreCards.delete(setupIntentId);
        }
      });

    if (setupIntentId) {
      pendingRegistrationStoreCards.set(setupIntentId, request);
    }

    return request;
  }

  //Store Card in backend
  static addCard(params) {
    Header.setHeaders();
    return axios
      .post(`${Url}stripe/store-card`, Encryption.encrypt(params))
      .then((res) => {
        return Encryption.decrypt(res.data);
      });
  }

  static stateList(params) {
    return axios.get(`${Url}common/state-list`).then((res) => {
      return Encryption.decrypt(res.data);
    });
  }

  static categoryList(language = "en", options = {}) {
    return runRetriedRequest(
      ({ signal, timeout }) =>
        axios.post(`${Url}common/category-list`, Encryption.encrypt({}), {
          headers: Header.getCommonRequestHeaders(language, API_SERVICE_ID),
          signal,
          timeout,
        }),
      options
    ).then((res) => Encryption.decrypt(res.data));
  }

  static checkRdAvailability(params, language = "en") {
    return axios
      .post(
        `${Url}registration/check-rd-availability`,
        Encryption.encrypt(params),
        { headers: Header.getCommonRequestHeaders(language, API_SERVICE_ID) }
      )
      .then((res) => Encryption.decrypt(res.data));
  }

  static listRegistrationMatchedRd(params, language = "en", options = {}) {
    const accessToken = getRegistrationAccessToken();
    if (!accessToken) {
      return Promise.reject(
        new Error("Session expired. Please restart registration.")
      );
    }
    const headers = {
      ...Header.getCommonRequestHeaders(language, API_SERVICE_ID),
      Authorization: accessToken,
    };
    return runRetriedRequest(
      ({ signal, timeout }) =>
        axios.post(`${Url}list-matched-rd`, Encryption.encrypt(params), {
          headers,
          signal,
          timeout,
        }),
      options
    ).then((res) => parseAppApiResponse(res.data));
  }

  static getRegistrationRdProfile(rdId, language = "en") {
    const accessToken = getRegistrationAccessToken();
    if (!accessToken) {
      return Promise.reject(
        new Error("Session expired. Please restart registration.")
      );
    }
    const headers = {
      ...Header.getCommonRequestHeaders(language, API_SERVICE_ID),
      Authorization: accessToken,
    };
    return axios
      .get(`${Url}common/rd-profile`, {
        params: { rd_id: String(rdId) },
        headers,
      })
      .then((res) => parseAppApiResponse(res.data));
  }

  static listRegistrationTimeslotByRd(params, language = "en", options = {}) {
    const accessToken = getRegistrationAccessToken();
    if (!accessToken) {
      return Promise.reject(
        new Error("Session expired. Please restart registration.")
      );
    }
    const headers = {
      ...Header.getCommonRequestHeaders(language, API_SERVICE_ID),
      Authorization: accessToken,
    };
    return runRetriedRequest(
      ({ signal, timeout }) =>
        axios.post(`${Url}list-timeslot-by-rd`, Encryption.encrypt(params), {
          headers,
          signal,
          timeout,
        }),
      options
    ).then((res) => parseAppApiResponse(res.data));
  }

  static listRegistrationRdTimeslot(params, language = "en", options = {}) {
    const accessToken = getRegistrationAccessToken();
    if (!accessToken) {
      return Promise.reject(
        new Error("Session expired. Please restart registration.")
      );
    }
    const headers = {
      ...Header.getCommonRequestHeaders(language, API_SERVICE_ID),
      Authorization: accessToken,
    };
    return runRetriedRequest(
      ({ signal, timeout }) =>
        axios.post(`${Url}list-rd-timeslot`, Encryption.encrypt(params), {
          headers,
          signal,
          timeout,
        }),
      options
    ).then((res) => parseAppApiResponse(res.data));
  }

  static unavailableStateLead(params, language = "en") {
    return axios
      .post(
        `${Url}registration/unavailable-state-lead`,
        Encryption.encrypt(params),
        { headers: Header.getCommonRequestHeaders(language, API_SERVICE_ID) }
      )
      .then((res) => Encryption.decrypt(res.data));
  }

  static listCalendlyAppointment(language = "en") {
    const accessToken = getRegistrationAccessToken();
    if (!accessToken) {
      return Promise.reject(
        new Error("Session expired. Please restart registration.")
      );
    }
    const headers = {
      ...Header.getCommonRequestHeaders(language, API_SERVICE_ID),
      Authorization: accessToken,
    };
    return axios
      .get(`${Url}calendly/list-appointment`, { headers })
      .then((res) => parseAppApiResponse(res.data));
  }

  static completeRegistrationBooking(params, language = "en") {
    const accessToken = getRegistrationAccessToken();
    if (!accessToken) {
      return Promise.reject(
        new Error("Session expired. Please restart registration.")
      );
    }
    const headers = {
      ...Header.getCommonRequestHeaders(language, API_SERVICE_ID),
      Authorization: accessToken,
    };
    return axios
      .post(`${Url}registration/complete-booking`, Encryption.encrypt(params), {
        headers,
      })
      .then((res) => parseAppApiResponse(res.data));
  }

  static getCancellationPolicy(language = "en", options = {}) {
    return runRetriedRequest(
      ({ signal, timeout }) =>
        axios.get(`${Url}registration/cancellation-policy`, {
          headers: Header.getCommonRequestHeaders(language, API_SERVICE_ID),
          signal,
          timeout,
        }),
      options
    ).then((res) => parseAppApiResponse(res.data));
  }
}
